import { useState } from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LeaveCancellationSchema,
  type LeaveCancellationDto,
  type LeaveRequestDto,
} from '@taqon/contracts';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { z } from 'zod';
import { getData, postJson } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { PersonName } from '../../shared/ui/person';
import { ApprovalPanel } from '../../shared/workflow/ApprovalPanel';
import { Facts, RecordFailure, RecordLink } from '../finance/detail';
import { durationLabel, LEAVE_REQUESTS } from './leave';

/**
 * Cancelling approved leave (approved R1 decision, 28 Sep 2026; FRD-HRP-030). The employee — or HR
 * on their behalf — asks to cancel an approved leave request with a reason; the request goes
 * through its own approval route; once approved the leave is cancelled and the days return to the
 * balance. Rejected or withdrawn, the leave stays approved. The leave keeps its own history.
 */
export const LEAVE_CANCELLATIONS = '/api/v1/hr/leave-cancellations';

const cancellationsKey = (leaveId: string) => ['hr', 'leave', 'cancellations', leaveId] as const;

export function cancellationPath(id: string): string {
  return `/hr/leave/cancellations/${encodeURIComponent(id)}`;
}

/** A leave request's cancellation requests, for a session that may view them. */
export function useLeaveCancellations(leaveId: string) {
  const session = useSession();
  return useQuery({
    queryKey: cancellationsKey(leaveId),
    enabled: leaveId !== '' && hasCapability(session.data, 'hr.leave_cancellation:view'),
    queryFn: () =>
      getData(
        `${LEAVE_REQUESTS}/${encodeURIComponent(leaveId)}/cancellations`,
        z.array(LeaveCancellationSchema),
      ),
  });
}

/** On an approved leave request: its cancellation requests, and the action to ask for one. */
export function LeaveCancellationsSection({ request }: { request: LeaveRequestDto }) {
  const session = useSession();
  const canView = hasCapability(session.data, 'hr.leave_cancellation:view');
  const canCreate = hasCapability(session.data, 'hr.leave_cancellation:create');
  const [asking, setAsking] = useState(false);
  const cancellations = useLeaveCancellations(request.id);
  const rows = cancellations.data ?? [];
  const open = rows.some((row) => row.status === 'submitted' || row.status === 'returned');
  const mayAsk = canCreate && request.status === 'approved' && !open;
  if (!canView && !mayAsk) return null;
  return (
    <Section
      title="Cancellation"
      actions={
        mayAsk ? (
          <Button
            variant="outlined"
            color="warning"
            onClick={(event) => {
              event.currentTarget.focus();
              setAsking(true);
            }}
          >
            Request cancellation
          </Button>
        ) : undefined
      }
    >
      <Stack spacing={1.5} sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Approved leave is cancelled through its own approval. Until a cancellation is approved the
          leave stays in effect; once it is, the leave is cancelled and its days return to the
          balance.
        </Typography>
        {cancellations.isPending && canView && <LoadingState label="Loading cancellations" />}
        {canView && !cancellations.isPending && rows.length === 0 && (
          <EmptyState title="No cancellation requested" />
        )}
        {rows.length > 0 && (
          <Table size="small" aria-label="Cancellation requests">
            <TableHead>
              <TableRow>
                <TableCell>Number</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Submitted</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <RecordLink to={cancellationPath(row.id)}>{row.code}</RecordLink>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={row.status} />
                  </TableCell>
                  <TableCell>{row.reason}</TableCell>
                  <TableCell>{new Date(row.submittedAt).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Stack>
      {asking && (
        <ReasonDialog
          title={`Request cancellation of ${request.code}`}
          submitLabel="Request cancellation"
          intro={`The ${request.leaveTypeName.toLowerCase()} from ${request.startDate} to ${request.endDate} (${durationLabel(request)}) stays approved until the cancellation is approved.`}
          path={`${LEAVE_REQUESTS}/${encodeURIComponent(request.id)}/cancellations`}
          leaveId={request.id}
          onClose={() => {
            setAsking(false);
          }}
        />
      )}
    </Section>
  );
}

/** A reason, sent to ask for a cancellation or to resubmit a returned one. */
function ReasonDialog({
  title,
  submitLabel,
  intro,
  path,
  leaveId,
  initial = '',
  expectedVersion,
  onClose,
}: {
  title: string;
  submitLabel: string;
  intro: string;
  path: string;
  leaveId: string;
  initial?: string;
  expectedVersion?: number;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [reason, setReason] = useState(initial);
  const send = useMutation({
    mutationFn: () =>
      postJson(
        path,
        { reason: reason.trim() },
        LeaveCancellationSchema,
        session.data?.csrfToken ?? '',
        expectedVersion === undefined ? undefined : { expectedVersion },
      ),
    onSuccess: async (row) => {
      await queryClient.invalidateQueries({ queryKey: ['hr', 'leave'] });
      await queryClient.invalidateQueries({ queryKey: ['workflow'] });
      await queryClient.invalidateQueries({ queryKey: cancellationsKey(leaveId) });
      onClose();
      void navigate(cancellationPath(row.id));
    },
  });
  return (
    <FormDialog
      open
      title={title}
      submitLabel={submitLabel}
      canSubmit={reason.trim() !== ''}
      submitting={send.isPending}
      error={send.isError ? send.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        send.mutate();
      }}
    >
      <Typography variant="body2">{intro}</Typography>
      <TextField
        label="Reason"
        required
        multiline
        minRows={2}
        value={reason}
        onChange={(e) => {
          setReason(e.target.value);
        }}
        slotProps={{ htmlInput: { maxLength: 2000 } }}
      />
    </FormDialog>
  );
}

/**
 * One cancellation request (`/hr/leave/cancellations/:id`): its facts and the leave it cancels,
 * the approval history with the approvers' decisions, its audit history, and the requester's
 * actions — withdraw while in approval, resubmit once returned.
 */
export function LeaveCancellationDetailPage() {
  const { id = '' } = useParams();
  const session = useSession();
  const queryClient = useQueryClient();
  const [withdrawing, setWithdrawing] = useState(false);
  const [resubmitting, setResubmitting] = useState(false);
  const detail = useQuery({
    queryKey: ['hr', 'leave', 'cancellation', id],
    queryFn: () =>
      getData(`${LEAVE_CANCELLATIONS}/${encodeURIComponent(id)}`, LeaveCancellationSchema),
  });
  const back = (
    <Button
      component={RouterLink}
      to={
        detail.data === undefined
          ? '/hr/leave'
          : `/hr/leave/requests/${encodeURIComponent(detail.data.leaveRequestId)}`
      }
      startIcon={<ArrowBackIcon />}
    >
      {detail.data === undefined ? 'My leave' : `Leave request ${detail.data.leaveCode}`}
    </Button>
  );
  if (detail.isPending) return <LoadingState label="Loading the cancellation" />;
  if (detail.isError) {
    return (
      <RecordFailure
        title="Leave cancellation"
        noun="leave cancellation"
        error={detail.error}
        back={back}
      />
    );
  }
  const row: LeaveCancellationDto = detail.data;
  const isRequester = session.data?.userId === row.requestedBy;
  const canChange = hasCapability(session.data, 'hr.leave_cancellation:change');
  return (
    <Stack spacing={3}>
      <PageHeader
        title={`Leave cancellation ${row.code}`}
        description={`${row.employeeName} · ${row.leaveTypeName}, ${row.startDate} to ${row.endDate}`}
        actions={
          <>
            {back}
            {row.status === 'submitted' && (isRequester || canChange) && (
              <Button
                variant="outlined"
                color="warning"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setWithdrawing(true);
                }}
              >
                Withdraw
              </Button>
            )}
            {row.status === 'returned' && isRequester && (
              <Button
                variant="contained"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setResubmitting(true);
                }}
              >
                Edit and resubmit
              </Button>
            )}
          </>
        }
      />
      <Section title="Cancellation">
        <Facts
          label="Leave cancellation details"
          facts={[
            ['Status', <StatusChip key="s" status={row.status} />],
            ['Number', row.code],
            [
              'Leave request',
              <RecordLink
                key="l"
                to={`/hr/leave/requests/${encodeURIComponent(row.leaveRequestId)}`}
              >
                {row.leaveCode}
              </RecordLink>,
            ],
            ['Leave status', <StatusChip key="ls" status={row.leaveStatus} />],
            [
              'Employee',
              <PersonName key="e" name={row.employeeName} employeeId={row.employeeId}>
                {row.employeeCode} — {row.employeeName}
              </PersonName>,
            ],
            ['Leave', `${row.leaveTypeName}, ${row.startDate} to ${row.endDate}`],
            ['Duration', durationLabel(row)],
            ['Reason', row.reason],
            ['Submitted', new Date(row.submittedAt).toLocaleString()],
            ['Decided', row.decidedAt === null ? '—' : new Date(row.decidedAt).toLocaleString()],
          ]}
        />
      </Section>
      <ApprovalPanel
        recordType="hr.leave_cancellation"
        recordId={row.id}
        onDecided={() => {
          void queryClient.invalidateQueries({ queryKey: ['hr', 'leave'] });
        }}
      />
      <RecordHistory recordType="hr.leave_cancellation" recordId={row.id} />
      {withdrawing && (
        <WithdrawDialog
          row={row}
          onClose={() => {
            setWithdrawing(false);
          }}
        />
      )}
      {resubmitting && (
        <ReasonDialog
          title={`Resubmit ${row.code}`}
          submitLabel="Resubmit"
          intro="The cancellation goes back into approval with the reason below."
          path={`${LEAVE_CANCELLATIONS}/${encodeURIComponent(row.id)}/resubmit`}
          leaveId={row.leaveRequestId}
          initial={row.reason}
          expectedVersion={row.version}
          onClose={() => {
            setResubmitting(false);
          }}
        />
      )}
    </Stack>
  );
}

function WithdrawDialog({ row, onClose }: { row: LeaveCancellationDto; onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const withdraw = useMutation({
    mutationFn: () =>
      postJson(
        `${LEAVE_CANCELLATIONS}/${encodeURIComponent(row.id)}/cancel`,
        {},
        LeaveCancellationSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: row.version },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hr', 'leave'] });
      await queryClient.invalidateQueries({ queryKey: ['workflow'] });
      onClose();
    },
  });
  return (
    <ConfirmDialog
      open
      title={`Withdraw ${row.code}?`}
      message={`The request to cancel leave ${row.leaveCode} is withdrawn from approval. The leave stays approved.`}
      confirmLabel="Withdraw"
      tone="warning"
      busy={withdraw.isPending}
      error={withdraw.isError ? withdraw.error.message : null}
      onCancel={onClose}
      onConfirm={() => {
        withdraw.mutate();
      }}
    />
  );
}
